import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { format, addDays, addSeconds, startOfMonth } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { getAPIUrl } from '../api/apiUtils';
import { regionsStore } from '../stores';
import { groupIdToTitle } from '../utils/groupTitles';
import { EMOTIVE_THEMES, DEFAULT_THEMES, THEME_LABEL_BY_KEY } from '../utils/themes';

// Вспомогательная функция для безопасного создания даты
const safeCreateDate = (value) => {
  if (value === undefined || value === null || value === '') return null;
  
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

// Вспомогательная функция для безопасного форматирования даты
const safeFormatDate = (dateValue, formatStr) => {
  const d = safeCreateDate(dateValue);
  if (!d) return null;
  
  try {
    return format(d, formatStr, { locale: ru });
  } catch {
    return null;
  }
};

// Функция для преобразования времени в формат 'YYYY-MM-DD'
// Поддерживает как Unix timestamp в секундах, так и ISO-строку
function formatDate(timestamp) {
  if (timestamp == null) return null;

  let date;
  if (typeof timestamp === 'number') {
    // старый формат: секунды
    date = new Date(timestamp * 1000);
  } else {
    // новый формат: ISO-строка, например "2025-01-18T14:45:33+00:00"
    date = new Date(timestamp);
  }

  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}

// Функция для построения ссылки на пост ВКонтакте
function getVkPostLink(groupId, postId) {
  if (groupId == null || postId == null) return null;
  
  try {
    const numericGroupId = Number(groupId);
    if (Number.isNaN(numericGroupId)) return null;
    
    const vkGroupId = numericGroupId > 0 ? -numericGroupId : numericGroupId;
    return `https://vk.com/wall${vkGroupId}_${postId}`;
  } catch {
    return null;
  }
}

// Функция для парсинга даты из метки интервала
function parseDateLabel(dateLabel, aggregation) {
  if (!dateLabel || typeof dateLabel !== 'string') return null;
  
  try {
    // Если это формат YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateLabel)) {
      const d = safeCreateDate(dateLabel);
      if (!d) return null;
      
      const dateStr = d.toISOString().slice(0, 10);
      return { startDate: dateStr, endDate: dateStr };
    }
    
    // Если это формат недели: (YYYY-MM-DD - YYYY-MM-DD) или YYYY-MM-DD - YYYY-MM-DD
    if (aggregation === 7 && dateLabel.includes(' - ')) {
      // Пробуем формат со скобками
      let match = dateLabel.match(/\((\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})\)/);
      if (!match) {
        // Пробуем формат без скобок
        match = dateLabel.match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
      }
      
      if (match) {
        const startD = safeCreateDate(match[1]);
        const endD = safeCreateDate(match[2]);
        
        if (startD && endD) {
          return {
            startDate: startD.toISOString().slice(0, 10),
            endDate: endD.toISOString().slice(0, 10)
          };
        }
      }
    }
    
    // Если это формат месяца: "январь 2024"
    if (aggregation === 30) {
      const monthNames = {
        'январь': 0, 'февраль': 1, 'март': 2, 'апрель': 3,
        'май': 4, 'июнь': 5, 'июль': 6, 'август': 7,
        'сентябрь': 8, 'октябрь': 9, 'ноябрь': 10, 'декабрь': 11
      };
      
      const parts = dateLabel.toLowerCase().split(' ');
      if (parts.length === 2 && monthNames[parts[0]] !== undefined) {
        const year = parseInt(parts[1], 10);
        const month = monthNames[parts[0]];
        
        if (!isNaN(year)) {
          const startDate = new Date(year, month, 1);
          const endDate = new Date(year, month + 1, 0);
          
          return {
            startDate: startDate.toISOString().slice(0, 10),
            endDate: endDate.toISOString().slice(0, 10)
          };
        }
      }
    }
  } catch (e) {
    console.error('Ошибка парсинга даты:', e);
  }
  
  return null;
}

// Функция для построения строки таблицы из элемента данных
function buildModalRow(item) {
  if (!item) return null;
  
  try {
    const timestamp = item.date;
    const dateObj = safeCreateDate(
      typeof timestamp === 'number' ? timestamp * 1000 : timestamp
    );
    
    if (!dateObj) return null;

    const formattedDate = dateObj.toISOString().slice(0, 10);
    const groupId = item.group_id != null ? String(item.group_id) : '-';
    const postId = item.post_id ?? '-';
    const commentId = item.comment_id ?? '-';
    const authorId = item.author_id ?? '-';
    const groupTitle = groupIdToTitle[groupId] || groupId;

    const link = getVkPostLink(groupId, postId);
    const commentLink = link && commentId !== '-' ? `${link}?reply=${commentId}` : null;

    const row = {
      'Дата': formattedDate,
      'Название сообщества': groupTitle,
      'ID поста': postId,
      'ID комментария': commentId,
      'Автор': authorId !== '-' ? `https://vk.com/id${authorId}` : '-',
      'Текст': item.text ?? '-',
      'Ссылка на пост': link || '-',
      'Ссылка на комментарий': commentLink || '-',
    };

    // Добавляем значение выбранной темы, если оно есть
    if (item.selectedTheme && item[item.selectedTheme] !== undefined) {
      const themeLabel = THEME_LABEL_BY_KEY[item.selectedTheme] || item.selectedTheme;
      row[themeLabel] = item[item.selectedTheme];
    }

    return row;
  } catch (e) {
    console.error('Ошибка построения строки таблицы:', e);
    return null;
  }
}

// Функция для группировки и преобразования данных с усреднением по датам
function transformData(inputData, selectedThemes, selectedTheme) {
  if (!Array.isArray(inputData)) {
    console.error('Ошибка: inputData не является массивом', inputData);
    return [];
  }

  // Собираем все уникальные даты из данных
  const allDates = new Set();
  inputData.forEach(item => {
    if (item && item.date) {
      const formattedDate = formatDate(item.date);
      if (formattedDate) {
        allDates.add(formattedDate);
      }
    }
  });
  
  const sortedDates = Array.from(allDates).sort();

  // Группировка по group_id
  const groupedByGroupId = inputData.reduce((acc, item) => {
    if (!item || !item.group_id || !item.date || item[selectedTheme] === undefined) {
      console.warn('Некорректный элемент данных:', item);
      return acc;
    }

    const { group_id, date, [selectedTheme]: value } = item;
    const formattedDate = formatDate(date);
    
    if (!formattedDate) return acc;

    if (!acc[group_id]) {
      acc[group_id] = {};
    }
    if (!acc[group_id][formattedDate]) {
      acc[group_id][formattedDate] = { sum: 0, count: 0 };
    }
    acc[group_id][formattedDate].sum += value;
    acc[group_id][formattedDate].count += 1;

    return acc;
  }, {});

  // Формируем данные для каждого маркера, включая null для отсутствующих дат
  return Object.keys(groupedByGroupId).map(group_id => {
    const title = groupIdToTitle[group_id] || group_id.toString();
    const data = sortedDates.map(date => {
      const entry = groupedByGroupId[group_id][date];
      return {
        date,
        value: entry ? entry.sum / entry.count : null
      };
    });

    return {
      data,
      marker: title
    };
  });
}

class CommunityEmotionsStore {
  aggregation = 1;
  selectedThemes = DEFAULT_THEMES.slice();
  selectedTheme = EMOTIVE_THEMES[0].key;
  chartsData = [];
  loading = true;
  error = null;
  globalFilters = { selectedGroups: [], startDate: null, endDate: null };
  globalGroups = [];
  lastQueryStartDate = null;
  lastQueryEndDate = null;
  lastQueryAggregation = null;
  lastQueryGroups = [];
  lastQueryThemes = [];
  lastEndQuery = null;

  constructor() {
    makeAutoObservable(this);
  }

  setAggregation(val) { this.aggregation = val; }
  setThemes(val) { this.selectedThemes = val; }
  setSelectedTheme(val) { this.selectedTheme = val; }
  setGlobalFilters(val) { this.globalFilters = val; }
  setGlobalGroups(val) { this.globalGroups = val; }

  setLastQueryParams({ startDate, endDate, aggregation, selectedGroups, selectedThemes }) {
    this.lastQueryStartDate = startDate;
    this.lastQueryEndDate = endDate;
    this.lastQueryAggregation = aggregation;
    this.lastQueryGroups = selectedGroups;
    this.lastQueryThemes = selectedThemes;    
  }

  async fetchGroups() {
    try {
      await regionsStore.fetchRegions();
      const allGroups = regionsStore.allGroups.map(g => ({
        id: String(g.group_id),
        name: g.title
      }));
      runInAction(() => { this.globalGroups = allGroups; });
    } catch {
      runInAction(() => { this.globalGroups = []; });
    }
  }

  // Вспомогательная функция для безопасного преобразования параметров запроса
  safePrepareFetchParams(start, end) {
    // Проверяем типы параметров
    if (typeof start !== 'string' || typeof end !== 'string') {
      console.error('fetchTopicsTrendsData: start/end must be strings', { start, end });
      throw new Error('fetchTopicsTrendsData: start/end must be strings');
    }

    // Создаем даты с проверкой
    const s = safeCreateDate(start);
    const e = safeCreateDate(end);

    if (!s || !e) {
      console.error('fetchTopicsTrendsData: Invalid date value', { start, end });
      throw new Error('fetchTopicsTrendsData: Invalid date value');
    }

    return { s, e };
  }

  async fetchTopicsTrendsData(start, end) {
    try {
      // ВЕРСИЯ 2: Принимаем и числа, и строки
      // Если переданы числа (индексы), то используем их как смещение
      // Если переданы строки (даты), то используем их напрямую
      
      let startDate, endDate;
      
      // Проверяем тип параметров
      if (typeof start === 'number' && typeof end === 'number') {
        // Параметры - индексы (смещения)
        const startOffset = start;
        const endOffset = end;
        
        // Базовые даты из глобальных фильтров
        const baseStartDate = this.globalFilters.startDate 
          ? safeCreateDate(this.globalFilters.startDate)
          : safeCreateDate(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
        
        const baseEndDate = this.globalFilters.endDate 
          ? safeCreateDate(this.globalFilters.endDate)
          : new Date();
        
        if (!baseStartDate || !baseEndDate) {
          throw new Error('Invalid base date range');
        }
        
        // Вычисляем даты на основе индексов и агрегации
        startDate = new Date(baseStartDate);
        startDate.setDate(startDate.getDate() + (startOffset * this.aggregation));
        
        endDate = new Date(baseStartDate);
        endDate.setDate(endDate.getDate() + (endOffset * this.aggregation));
        
      } else if (typeof start === 'string' && typeof end === 'string') {
        // Параметры - строки дат
        startDate = safeCreateDate(start);
        endDate = safeCreateDate(end);
        
        if (!startDate || !endDate) {
          throw new Error('fetchTopicsTrendsData: Invalid date string format');
        }
      } else {
        console.error('fetchTopicsTrendsData: Invalid parameter types', { 
          start, end, 
          startType: typeof start, 
          endType: typeof end 
        });
        throw new Error('fetchTopicsTrendsData: Parameters must be both numbers or both strings');
      }
      
      // Собираем текущие параметры с защитой
      const currentParams = {
        startDate: this.globalFilters.startDate 
          ? safeCreateDate(this.globalFilters.startDate) 
          : new Date(Date.now() - 30 * 86400000),
        endDate: this.globalFilters.endDate 
          ? safeCreateDate(this.globalFilters.endDate) 
          : new Date(),
        aggregation: this.aggregation,
        selectedGroups: this.globalFilters.selectedGroups.slice(),
        selectedThemes: this.selectedThemes.slice(),
      };
  
      // Проверяем, изменились ли параметры
      const changed =
        this.lastQueryAggregation !== currentParams.aggregation ||
        JSON.stringify(this.lastQueryGroups) !== JSON.stringify(currentParams.selectedGroups) ||
        JSON.stringify(this.lastQueryThemes) !== JSON.stringify(currentParams.selectedThemes) ||
        (this.lastQueryStartDate && currentParams.startDate && 
          this.lastQueryStartDate.getTime() !== currentParams.startDate.getTime()) ||
        (this.lastQueryEndDate && currentParams.endDate && 
          this.lastQueryEndDate.getTime() !== currentParams.endDate.getTime());
  
      if (changed) {
        runInAction(() => {
          this.chartsData = [];
          this.lastEndQuery = null;
          this.loading = true;
        });
      }
  
      // Ограничиваем дату окончания глобальным фильтром
      if (this.globalFilters.endDate) {
        const globalEnd = safeCreateDate(this.globalFilters.endDate);
        if (globalEnd && endDate.getTime() > globalEnd.getTime()) {
          endDate = globalEnd;
        }
      }
  
      runInAction(() => {
        this.error = null;
      });
  
      const { selectedGroups } = this.globalFilters;
      if (!selectedGroups.length || !startDate || !endDate) {
        runInAction(() => {
          this.chartsData = [];
          this.loading = false;
        });
        return;
      }
  
      // Проверка на необходимость запроса
      if (this.lastEndQuery && this.lastEndQuery.getTime() >= endDate.getTime()) {
        runInAction(() => {
          this.loading = false;
        });
        return;
      }
  
      this.loading = true;
  
      // Подготавливаем параметры запроса с безопасным форматированием
      const startDateForQuery = this.lastEndQuery || startDate;
      const formattedStart = safeFormatDate(startDateForQuery, 'yyyy-MM-dd');
      const formattedEnd = safeFormatDate(endDate, 'yyyy-MM-dd');
  
      if (!formattedStart || !formattedEnd) {
        throw new Error('Не удалось сформатировать даты для запроса');
      }
  
      const params = {
        group_ids: selectedGroups.join(','),
        sort_by: 'date',
        sort_order: 'asc',
        columns: [this.selectedTheme, 'date', 'group_id'].join(','),
        start_date: formattedStart,
        end_date: formattedEnd,
        limit: 10000000
      };
  
      console.log('Запрос с параметрами:', params);
  
      const response = await axios.get(`${getAPIUrl()}/detection/text_markers_analysis`, { params });
      const data = response.data?.data || [];
  
      // Преобразуем данные
      let transformedData = transformData(data, this.selectedThemes, this.selectedTheme);
      
      // Агрегация по неделям
      if (this.aggregation === 7) {
        transformedData = transformedData.map(series => {
          const weeklyData = {};
          series.data.forEach(point => {
            const date = safeCreateDate(point.date);
            if (!date) return;
            
            // Определяем начало недели (понедельник)
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1));
            const weekEnd = addDays(weekStart, 6);
            const weekLabel = `(${weekStart.toISOString().slice(0, 10)} - ${weekEnd.toISOString().slice(0, 10)})`;
  
            if (!weeklyData[weekLabel]) {
              weeklyData[weekLabel] = { sum: 0, count: 0 };
            }
            weeklyData[weekLabel].sum += point.value;
            weeklyData[weekLabel].count += 1;
          });
  
          return {
            ...series,
            data: Object.keys(weeklyData).map(weekLabel => ({
              date: weekLabel,
              value: weeklyData[weekLabel].sum / weeklyData[weekLabel].count
            }))
          };
        });
      }
      
      // Агрегация по месяцам
      if (this.aggregation === 30) {
        transformedData = transformedData.map(series => {
          const monthlyData = {};
          series.data.forEach(point => {
            const date = safeCreateDate(point.date);
            if (!date) return;
            
            // Определяем начало месяца
            const monthStart = startOfMonth(date);
            const monthLabel = format(monthStart, 'LLLL yyyy', { locale: ru });
  
            if (!monthlyData[monthLabel]) {
              monthlyData[monthLabel] = { sum: 0, count: 0 };
            }
            monthlyData[monthLabel].sum += point.value;
            monthlyData[monthLabel].count += 1;
          });
  
          return {
            ...series,
            data: Object.keys(monthlyData).map(monthLabel => ({
              date: monthLabel,
              value: monthlyData[monthLabel].sum / monthlyData[monthLabel].count
            }))
          };
        });
      }
  
      // Добавляем среднее значение
      if (transformedData.length > 1) {
        const allDates = new Set();
        transformedData.forEach(series =>
          series.data.forEach(p => allDates.add(p.date))
        );
  
        const sortedDates = Array.from(allDates).sort((a, b) => {
          const dateA = safeCreateDate(a.includes('(') ? a.match(/\d{4}-\d{2}-\d{2}/)?.[0] : a);
          const dateB = safeCreateDate(b.includes('(') ? b.match(/\d{4}-\d{2}-\d{2}/)?.[0] : b);
          
          if (!dateA || !dateB) return 0;
          return dateA.getTime() - dateB.getTime();
        });
  
        const avgSeries = {
          marker: "Среднее по сообществам",
          data: sortedDates.map(date => {
            const values = transformedData
              .filter(series => series.marker !== "Среднее по сообществам")
              .map(series => {
                const point = series.data.find(p => p.date === date);
                return point && point.value != null ? point.value : null;
              })
              .filter(value => value !== null);
  
            const avg = values.length > 0
              ? values.reduce((a, b) => a + b, 0) / values.length
              : null;
  
            return { date, value: avg };
          }),
        };
  
        transformedData = [
          ...transformedData.filter(s => s.marker !== "Среднее по сообществам"),
          avgSeries,
        ];
      }
  
      runInAction(() => {
        // Мержим данные
        if (this.chartsData.length === 0) {
          this.chartsData = transformedData;
        } else {
          const merged = [...this.chartsData];
  
          transformedData.forEach(newSeries => {
            const existingSeries = merged.find(s => s.marker === newSeries.marker);
            if (existingSeries) {
              const existingDates = new Set(existingSeries.data.map(d => d.date));
              newSeries.data.forEach(point => {
                if (!existingDates.has(point.date)) {
                  existingSeries.data.push(point);
                }
              });
              
              // Сортируем по дате
              existingSeries.data.sort((a, b) => {
                const dateA = safeCreateDate(a.date.includes('(') ? a.date.match(/\d{4}-\d{2}-\d{2}/)?.[0] : a.date);
                const dateB = safeCreateDate(b.date.includes('(') ? b.date.match(/\d{4}-\d{2}-\d{2}/)?.[0] : b.date);
                
                if (!dateA || !dateB) return 0;
                return dateA.getTime() - dateB.getTime();
              });
            } else {
              merged.push(newSeries);
            }
          });
  
          this.chartsData = merged;
        }
  
        // Сохраняем end_date текущего запроса
        if (!this.lastEndQuery || this.lastEndQuery.getTime() < endDate.getTime()) {
          this.lastEndQuery = endDate;
        }
        
        this.setLastQueryParams(currentParams);
        this.loading = false;
      });
  
    } catch (e) {
      console.error('Ошибка в fetchTopicsTrendsData:', e);
      
      runInAction(() => {
        this.error = 'Ошибка загрузки данных: ' + (e.response?.data?.error || e.message);
        this.chartsData = [];
        this.loading = false;
      });
    }
  }

  async fetchTableDataByDate(dateLabel) {
    if (!dateLabel || typeof dateLabel !== 'string') return [];
    
    try {
      const { selectedGroups } = this.globalFilters;
      if (!selectedGroups.length) {
        return [];
      }

      // Парсим дату из метки интервала
      const dateRange = parseDateLabel(dateLabel, this.aggregation);
      if (!dateRange) {
        console.warn('Не удалось распарсить дату:', dateLabel);
        return [];
      }

      // Формируем список колонок
      const requiredColumns = ['group_id', 'post_id', 'comment_id', 'author_id', 'text', 'date'];
      const allColumns = Array.from(new Set([...requiredColumns, this.selectedTheme]));

      const params = {
        group_ids: selectedGroups.join(','),
        sort_by: 'date',
        sort_order: 'asc',
        columns: allColumns.join(','),
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        limit: 10000000
      };

      const response = await axios.get(`${getAPIUrl()}/detection/text_markers_analysis`, { params });
      const data = response.data?.data || [];

      // Преобразуем данные в формат для модального окна
      const rows = data
        .map(item => {
          const itemWithTheme = { ...item, selectedTheme: this.selectedTheme };
          return buildModalRow(itemWithTheme);
        })
        .filter(row => row !== null);
      
      return rows;
    } catch (e) {
      console.error('Ошибка при получении данных по дате:', e);
      return [];
    }
  }
}

export const communityEmotionsStore = new CommunityEmotionsStore();